/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useEffect, useMemo } from "react";
import { ApolloClient, useApolloClient } from "@apollo/client";
import dayjs from "dayjs";
import {
  OrgUnitEnvironmentBudgetDocument,
  OrgUnitEnvironmentBudgetQuery,
  useOrgUnitBudgetQuery,
  useOrgUnitChildrenBudgetQuery,
} from "~/generated/graphql";
import { generateBudget, sanitizeLastYearSpend } from "~/components/Budgets";
import { dateFormat } from "~/constants";
import { useConvertFromBaseCurrency, useCurrency } from "~/hooks";
import { generateNumericArray, isNotNil } from "~/tools";
import { useCreateBudget } from "./useCreateBudget";
import { useEditBudget } from "./useEditBudget";

export function useOrgUnitBudget(nid: string) {
  const { currency } = useCurrency();
  const client = useApolloClient();
  const { convert, rate } = useConvertFromBaseCurrency();

  const [year, setYear] = useState(dayjs().format(dateFormat.year));
  const [budget, setBudget] = useState<string[]>([]);
  const [isGenerated, setIsGenerated] = useState(false);
  const [envBudgets, setEnvBudgets] = useState<string[][]>([]);

  const { data, loading, error } = useOrgUnitBudgetQuery({
    variables: {
      nid,
      year,
      previousYear: (parseInt(year, 10) - 1).toString(),
    },
  });

  const { data: childrenData } = useOrgUnitChildrenBudgetQuery({
    variables: {
      nid,
      year,
    },
  });

  useEffect(() => {
    getEnvBudgets(client, nid, year).then(setEnvBudgets);
  }, [client, nid, year]);

  const sumOfChildrenBudgets = useMemo(() => {
    const childrenBudgets = childrenData?.orgUnitWithChildren?.children
      ?.map((orgUnit) => orgUnit?.budgetYearly.budgetByMonth.map(convert))
      .filter(isNotNil);

    return calculateChildrenBudget(childrenBudgets, envBudgets);
  }, [childrenData?.orgUnitWithChildren?.children, envBudgets]);

  const [createOrgUnitBudget] = useCreateBudget();
  const [updateOrgUnitBudget] = useEditBudget();

  const lastYearSpends = (data?.orgUnitV2?.spendYearly.spends ?? []).map(
    convert
  );
  const forecastSpendYearly = (
    data?.orgUnitV2?.forecastSpendYearly.spends ?? []
  ).map(convert);

  useEffect(() => {
    setBudget((data?.orgUnitV2?.budgetYearly.budgetByMonth ?? []).map(convert));
  }, [rate, setBudget, data]);

  return {
    budget,
    sumOfChildrenBudgets,
    spends: lastYearSpends.map(Number),
    forecast: forecastSpendYearly.map(Number),
    loading,
    error,
    createOrgUnitBudget: async (value: number) => {
      await createOrgUnitBudget({
        nid,
        year,
        budgetValue: value,
        currency,
      });
      setIsGenerated(false);
    },
    updateOrgUnitBudget: async (values: number[]) => {
      await updateOrgUnitBudget({ nid, year, budgetByMonth: values, currency });
      setIsGenerated(false);
    },
    getGeneratedBudget: (initial = true) => {
      setBudget(
        sanitizeLastYearSpend(
          generateBudget(lastYearSpends, forecastSpendYearly, year)
        )
      );
      setIsGenerated(initial);
    },
    reset: (cached: number[][]) => {
      if (isGenerated) {
        setBudget([]);
        setIsGenerated(false);
      } else {
        setBudget(cached.flat().map(String));
      }
    },
    isBudgetGenerated: isGenerated,
    year,
    setYear: (value: string) => {
      setYear(value);
      setIsGenerated(false);
    },
  };
}

function calculateChildrenBudget(
  childrenBudgets: string[][] | undefined,
  envBudgets: string[][]
) {
  const orgUnitBudget =
    childrenBudgets?.reduce(
      (prev, curr) =>
        prev.map((value, index) => value + parseFloat(curr[index])),
      generateNumericArray(12).map(() => 0)
    ) ?? [];

  const envBudget = envBudgets.reduce(
    (prev, curr) => prev.map((value, index) => value + parseFloat(curr[index])),
    generateNumericArray(12).map(() => 0)
  );

  return orgUnitBudget.map((value, index) => value + envBudget[index]);
}

async function getEnvBudgets(
  client: ApolloClient<unknown>,
  nid: string,
  year: string
) {
  const { data: firstRequestData } = await getOrgUnitEnvBudgetQuery(client, {
    nid,
    year,
    page: 0,
  });

  const pages = firstRequestData.orgUnitWithChildren?.environments?.pages || 1;

  const rest = await Promise.all(
    new Array(pages - 1).map((_, page) =>
      getOrgUnitEnvBudgetQuery(client, { year, nid, page: page + 1 })
    )
  );

  // `requestData` might be undefined although types don't reflect it, remove optional chainings with caution
  return [firstRequestData, ...rest.map((requestData) => requestData?.data)]
    .flatMap(
      (res) => res?.orgUnitWithChildren?.environments?.environments ?? []
    )
    .map((environment) => environment.budgetYearly.budgetByMonth);
}

function getOrgUnitEnvBudgetQuery(
  client: ApolloClient<unknown>,
  variables: { nid: string; year: string; page: number }
) {
  return client.query<OrgUnitEnvironmentBudgetQuery>({
    query: OrgUnitEnvironmentBudgetDocument,
    variables,
  });
}
